import { Flex } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { v4 as uuid } from 'uuid';
import { SidebarContent } from 'src/UI/app-container/SidebarContent';
import { FlowHeader } from 'src/UI/generate-flow/FlowHeader';
import { PillButton } from 'src/UI/generate-flow/PillButton';
import { FlowBody } from 'src/UI/generate-flow/FlowBody';
import { FlowLayout } from 'src/UI/generate-flow/FlowLayout';
import { SearchPill } from 'src/UI/generate-flow/SearchPill';
import { SiestaBarLayout } from 'src/UI/app-container/sidebar/SiestaBarLayout';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { AgendaClientStep } from './steps/AgendaClientStep';
import { AgendaTemplateStep } from './steps/AgendaTemplateStep';
import { AgendaProcessingStep } from './steps/AgendaProcessingStep';
import { AgendaSidebar } from './components/AgendaSidebar';
import { AgendaData, AgendaStepId, AgendaStepProps } from './types';

const initialData: AgendaData = {
  isFamily: false,
  client: undefined,
  template: undefined,
};

type StepDef = {
  id: AgendaStepId;
  title: string;
  disabledButtonlabel: string;
  render: (props: AgendaStepProps) => React.ReactNode;
  canContinue?: (data: AgendaData) => boolean;
  canFilter?: boolean;
};

export default function AgendaFlowPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const backHref = location.state?.fromPath ?? `/clients/all`;

  const { client: currentClient } = useCurrentClient();

  const flowId = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('flowId');
    if (fromUrl) return fromUrl;
    const fromState = location.state?.flowId;
    if (fromState) return fromState;
    const id = uuid();
    const url = new URL(window.location.href);
    url.searchParams.set('flowId', id);
    window.history.replaceState(null, '', url);
    return id;
  }, [location.state?.flowId]);

  const storageKey = `agenda-flow-${flowId}`;

  const [data, setData] = useState<AgendaData>(() => {
    const saved = sessionStorage.getItem(storageKey);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return initialData;
      }
    }
    return initialData;
  });

  const [stepIndex, setStepIndex] = useState(() => {
    const saved = sessionStorage.getItem(`${storageKey}-step`);
    return saved ? parseInt(saved, 10) : 0;
  });

  const [filter, setFilter] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    sessionStorage.setItem(storageKey, JSON.stringify(data));
  }, [data, storageKey]);

  useEffect(() => {
    sessionStorage.setItem(`${storageKey}-step`, String(stepIndex));
  }, [stepIndex, storageKey]);

  useEffect(() => {
    setFilter('');
  }, [stepIndex]);

  const steps: StepDef[] = useMemo(
    () => [
      {
        id: 'client',
        title: data.isFamily ? 'Select family' : 'Select client',
        disabledButtonlabel: data.isFamily ? 'Select the family' : 'Select the client',
        canContinue: d => (d.isFamily ? !!d.family?.id : !!d.client?.id),
        canFilter: true,
        render: props => <AgendaClientStep {...props} preselectClientId={currentClient?.id} />,
      },
      {
        id: 'template',
        title: 'Select template',
        disabledButtonlabel: 'Select template and continue',
        canContinue: d => !!d.template,
        render: props => <AgendaTemplateStep {...props} />,
      },
      {
        id: 'processing',
        title: 'Processing',
        disabledButtonlabel: 'Processing...',
        canContinue: () => true,
        canFilter: false,
        render: props => <AgendaProcessingStep {...props} onSubmittingChange={setIsSubmitting} />,
      },
    ],
    [data.isFamily, currentClient?.id],
  );

  const current = steps[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === steps.length - 1;

  const canContinue = (current.canContinue ? current.canContinue(data) : true) && !data.isLoading;
  const canFilter = current.canFilter ?? false;

  const resetCurrentStepSelection = () => {
    setData(prev => {
      switch (current.id) {
        case 'template':
          return { ...prev, template: undefined };
        default:
          return prev;
      }
    });
  };

  const goBack = () => {
    if (!isFirst) setStepIndex(i => i - 1);
    resetCurrentStepSelection();
  };

  const goNext = () => {
    if (!isLast) setStepIndex(i => i + 1);
  };

  const clearFlowStorage = () => {
    sessionStorage.removeItem(storageKey);
    sessionStorage.removeItem(`${storageKey}-step`);
  };

  const onClose = () => {
    clearFlowStorage();
    navigate(backHref);
  };

  const stepLabelMap: Partial<Record<AgendaStepId, string>> = {
    template: 'Generate agenda',
  };
  const continueLabel = !canContinue
    ? current.disabledButtonlabel
    : (stepLabelMap[current.id] ?? 'Continue');

  return (
    <>
      <Flex direction="column" h="100%" pos="relative">
        <FlowLayout
          header={
            !isLast ? (
              <FlowHeader onClose={onClose} onBack={stepIndex > 0 ? goBack : undefined} />
            ) : undefined
          }
          body={
            <FlowBody
              footerAlign="center"
              footer={
                <Flex direction="column" w={360}>
                  {current.id !== 'processing' && (
                    <PillButton
                      fullWidth
                      disabled={!canContinue}
                      label={continueLabel}
                      onClick={goNext}
                    />
                  )}
                </Flex>
              }
            >
              {current.render({
                data,
                setData,
                onNext: goNext,
                filter,
                searchBar: canFilter ? (
                  <SearchPill
                    value={filter}
                    onChange={setFilter}
                    placeholder={data.isFamily ? 'Search family by name' : 'Search client by name'}
                  />
                ) : undefined,
              })}
            </FlowBody>
          }
        />
      </Flex>
      <SidebarContent>
        <SiestaBarLayout>
          <AgendaSidebar
            stepIndex={stepIndex}
            steps={steps}
            data={data}
            currentStepId={current.id}
            isSubmitting={isSubmitting}
          />
        </SiestaBarLayout>
      </SidebarContent>
    </>
  );
}
